import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { barChartStylesDefault, IBarChartClassNames, IBarChartStyles } from './BarChart.styles';

const render = (
  el: HTMLElement,
  data: { [key: string]: any }[],
  labelFieldName: string,
  valueFieldNames: string[],
  colors: string[] = [],
  gradient?: {
    start?: string;
    end?: string;
  }
) => {
  const chart = new G2Chart({ container: el, autoFit: true });
  let style: { [key: string]: string } = {};
  if (colors[0]) {
    style = { fill: colors[0] };
  }
  if (gradient) {
    style = { fill: `linear-gradient(90deg, ${gradient.end} 0%, ${gradient.start} 100% )` };
  }
  chart.interval().data(data).encode('x', labelFieldName).encode('y', valueFieldNames[0]).style(style);
  chart.render();
};

export interface IBarChartProps extends IComponent<IBarChartStyles>, HTMLAttributes<HTMLDivElement>, IBaseChartProps {
  colors?: string[];
  gradient?: {
    start?: string;
    end?: string;
  };
}

export const BarChart = forwardRef((props: IBarChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const {
    className,
    styles,
    data = [],
    colors,
    gradient,
    labelFieldName = '',
    valueFieldNames = [],
    ...elementProps
  } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames, colors, gradient);
    }
  }, [data, labelFieldName, valueFieldNames, colors, gradient]);

  const classNames = useClassNames<Partial<IBarChartStyles>, IBarChartClassNames>(
    'BarChart',
    barChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
